/**
 * FamilyRoomModule — closed-circle messaging + presence + DND windows.
 *
 * v0 SCAFFOLD (this file):
 *   - Mounts a "Coming soon" surface that explains the concept and
 *     surfaces the family roster + presence dots from existing
 *     `/v1/family` data.
 *   - Calls `/v1/family-room/meta` (returns shape-correct placeholder)
 *     and `/v1/family-room/seen` (POST to mark room "seen now").
 *   - Reserves a hash route #/family-room and a registry entry so
 *     subsequent PRs can flesh out real comm-sdk integration without
 *     re-wiring the shell.
 *
 * v1 (future):
 *   - Realtime messaging via comm-sdk (Supabase Realtime broker).
 *   - Per-member DND windows + quiet hours respected by push fan-out.
 *   - Voice notes, reactions, locations-on-demand (opt-in per member).
 *
 * Spec: services/dispatch/dev-minipc1/dev-minipc1_Prompt_..._tier3-01-aweni-family-room.md
 *
 * Privacy: every endpoint requires JWT; user_id is taken from the token,
 * never trusted from the client. The room is scoped by family group
 * (existing `/v1/family` membership table).
 */

import type { AweniContext, AweniModule } from '../../types';

interface FamilyRoomMeta {
  ok?: boolean;
  data?: {
    user_id?: number;
    room_id?: string;
    member_count?: number;
    last_seen_at?: number;
    presence?: Array<{ user_id: number; name: string; online: boolean; last_seen?: number }>;
    unread?: number;
  };
}

export default class FamilyRoomModule implements AweniModule {
  private container: HTMLElement | null = null;
  private ctx: AweniContext | null = null;

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this.container = container;
    this.ctx = ctx;
    this._injectStyles();
    this._renderSkeleton();
    // Mark "seen" — fire-and-forget so subsequent visits show 0 unread.
    void this._markSeen();
    // Fetch + render meta after first paint.
    void this._fetchAndRender();
  }

  unmount(): void {
    if (this.container) this.container.innerHTML = '';
    this.container = null;
    this.ctx = null;
  }

  // ----------------------------------------------------------------
  // RENDER
  // ----------------------------------------------------------------

  private _renderSkeleton(): void {
    if (!this.container || !this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const isRtl = this.ctx.i18n.dir === 'rtl';
    this.container.innerHTML = `
      <div class="frm-shell" dir="${isRtl ? 'rtl' : 'ltr'}">
        <header class="frm-header">
          <h1 class="frm-title">${escHtml(t('family_room_title') || 'Family Room')}</h1>
          <p class="frm-subtitle">${escHtml(t('family_room_subtitle') || 'A closed circle for the people who matter.')}</p>
        </header>

        <section class="frm-presence-card">
          <div class="frm-card-head">
            <span class="frm-card-icon" aria-hidden="true">👥</span>
            <span class="frm-card-title">${escHtml(t('family_room_presence_title') || 'Right now')}</span>
            <span class="frm-card-meta" data-meta-presence>—</span>
          </div>
          <div class="frm-presence-list" data-presence-list>
            <div class="frm-skeleton-line"></div>
            <div class="frm-skeleton-line" style="width:70%;"></div>
          </div>
        </section>

        <section class="frm-soon-card">
          <div class="frm-card-head">
            <span class="frm-card-icon" aria-hidden="true">🚧</span>
            <span class="frm-card-title">${escHtml(t('family_room_soon_title') || 'Coming next')}</span>
          </div>
          <ul class="frm-soon-list">
            <li>${escHtml(t('family_room_soon_messaging') || 'Realtime messaging — text, voice, photo')}</li>
            <li>${escHtml(t('family_room_soon_dnd') || 'Per-member quiet hours respected by notifications')}</li>
            <li>${escHtml(t('family_room_soon_location') || 'Opt-in location-on-demand (one-tap, expires after 1h)')}</li>
            <li>${escHtml(t('family_room_soon_reactions') || 'Reactions + read receipts + typing indicators')}</li>
          </ul>
        </section>

        <section class="frm-cta-card">
          <p class="frm-cta-text">${escHtml(t('family_room_invite_hint') || 'Invite your family to add the people who will join the room.')}</p>
          <a href="#/family" class="frm-cta-btn">${escHtml(t('family_room_open_family') || 'Manage family →')}</a>
        </section>
      </div>
    `;
  }

  private async _fetchAndRender(): Promise<void> {
    if (!this.container) return;
    try {
      const r = await fetch('/aweni/api/v1/family-room/meta', {
        credentials: 'include',
        cache: 'no-store',
      });
      if (!r.ok) {
        this._renderEmptyPresence();
        return;
      }
      const data = (await r.json()) as FamilyRoomMeta;
      const meta = data.data;
      if (!meta) { this._renderEmptyPresence(); return; }
      this._renderPresence(meta);
    } catch {
      this._renderEmptyPresence();
    }
  }

  private _renderPresence(meta: NonNullable<FamilyRoomMeta['data']>): void {
    if (!this.container) return;
    const list = this.container.querySelector<HTMLElement>('[data-presence-list]');
    const metaEl = this.container.querySelector<HTMLElement>('[data-meta-presence]');
    if (!list) return;
    const presence = meta.presence || [];
    if (metaEl) {
      const onlineCount = presence.filter((p) => p.online).length;
      metaEl.textContent = `${onlineCount}/${presence.length} ${this.ctx?.i18n.t('family_room_online') || 'online'}`;
    }
    if (presence.length === 0) {
      list.innerHTML = `<div class="frm-empty">${escHtml(this.ctx?.i18n.t('family_room_no_members') || 'Add family members to start.')}</div>`;
      return;
    }
    list.innerHTML = presence.map((p) => `
      <div class="frm-presence-row">
        <span class="frm-dot ${p.online ? 'is-online' : ''}" aria-hidden="true"></span>
        <span class="frm-name">${escHtml(p.name || '—')}</span>
        <span class="frm-last">${p.online ? (this.ctx?.i18n.t('family_room_online') || 'online') : ''}</span>
      </div>
    `).join('');
  }

  private _renderEmptyPresence(): void {
    if (!this.container) return;
    const list = this.container.querySelector<HTMLElement>('[data-presence-list]');
    if (list) {
      list.innerHTML = `<div class="frm-empty">${escHtml(this.ctx?.i18n.t('family_room_offline') || 'Will load when connected.')}</div>`;
    }
  }

  private async _markSeen(): Promise<void> {
    try {
      await fetch('/aweni/api/v1/family-room/seen', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ at: Date.now() }),
        keepalive: true,
      });
    } catch { /* silent */ }
  }

  // ----------------------------------------------------------------
  // STYLES (single-shot inject — once per session)
  // ----------------------------------------------------------------

  private _injectStyles(): void {
    if (document.getElementById('frm-styles')) return;
    const s = document.createElement('style');
    s.id = 'frm-styles';
    s.textContent = `
      .frm-shell {
        max-width: 720px; margin: 0 auto; padding: 16px 16px 96px;
        display: flex; flex-direction: column; gap: 14px;
      }
      .frm-header { padding: 8px 4px 0; }
      .frm-title {
        font-size: 22px; font-weight: 800; margin: 0 0 4px;
        color: var(--ln-text-primary, var(--text-primary, #1E293B));
      }
      .dark .frm-title { color: #F1F5F9; }
      .frm-subtitle {
        font-size: 13px; color: var(--ln-text-secondary, var(--text-secondary, #6b6657));
        margin: 0; line-height: 1.5;
      }
      .frm-presence-card, .frm-soon-card, .frm-cta-card {
        background: var(--ln-bg-card, var(--card-bg, #fff));
        border: 1px solid var(--ln-border, var(--border, rgba(0,0,0,0.06)));
        border-radius: 16px; padding: 14px;
      }
      .frm-card-head {
        display: flex; align-items: center; gap: 8px; margin-bottom: 10px;
      }
      .frm-card-icon { font-size: 18px; }
      .frm-card-title {
        font-size: 13px; font-weight: 700;
        color: var(--ln-text-primary, var(--text-primary, #1E293B));
      }
      .frm-card-meta {
        margin-inline-start: auto; font-size: 11px;
        color: var(--ln-text-secondary, var(--text-secondary, #6b6657));
      }
      .frm-presence-list { display: flex; flex-direction: column; gap: 6px; min-height: 24px; }
      .frm-presence-row {
        display: flex; align-items: center; gap: 10px;
        padding: 6px 4px; border-radius: 8px;
      }
      .frm-dot {
        width: 10px; height: 10px; border-radius: 999px;
        background: var(--ln-border, rgba(0,0,0,0.18));
        flex-shrink: 0;
      }
      .frm-dot.is-online { background: #22c55e; box-shadow: 0 0 0 3px rgba(34,197,94,0.18); }
      .frm-name { font-size: 13px; font-weight: 600; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .frm-last { font-size: 11px; color: var(--ln-text-secondary, var(--text-secondary, #6b6657)); }
      .frm-soon-list {
        margin: 0; padding-inline-start: 18px;
        font-size: 13px; line-height: 1.6;
        color: var(--ln-text-primary, var(--text-primary, #1E293B));
      }
      .frm-cta-card { display: flex; flex-direction: column; gap: 10px; align-items: flex-start; }
      .frm-cta-text { font-size: 13px; margin: 0; line-height: 1.5; color: var(--ln-text-secondary, var(--text-secondary, #6b6657)); }
      .frm-cta-btn {
        align-self: stretch; text-align: center;
        padding: 12px; min-height: 48px;
        background: var(--ln-accent, var(--accent, #0A0A0A));
        color: var(--ln-accent-text, #fff);
        border-radius: 12px; font-size: 14px; font-weight: 700;
        text-decoration: none;
      }
      .frm-cta-btn:active { transform: scale(0.98); }
      .frm-skeleton-line {
        height: 14px; background: var(--ln-bg-secondary, rgba(0,0,0,0.04));
        border-radius: 7px; animation: skeletonPulse 1.2s ease-in-out infinite;
      }
      .frm-empty {
        font-size: 12px; color: var(--ln-text-secondary, var(--text-secondary, #6b6657));
        padding: 6px 4px;
      }
    `;
    document.head.appendChild(s);
  }
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
